import {
  type CSSProperties,
  type FormEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { ApiClientError } from "@/lib/api";
import {
  getAuthMessages,
  mcpScopeExplanationMessage,
  mcpScopeLabelMessage,
  scopeExplanationMessage,
  scopeLabelMessage,
  type AuthMessages,
} from "@/lib/authI18n";
import {
  CheckCircle2,
  KeyRound,
  ShieldCheck,
} from "lucide-react";
import { ErrorBox, ProgressRing, Spinner } from "@/components/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  resetTurnstileWidgets,
  TurnstileWidget,
} from "@/components/TurnstileWidget";
import {
  authenticateHostedPortal,
  changeHostedAccount,
  completeHostedEmailAction,
  createPlatformPortalContext,
  decideHostedAuthorization,
  decideMCPAuthorization,
  getEmailActionContext,
  getMCPAuthorizationContext,
  getProjectPortalContext,
  listOwnedProjectsForMCP,
  requestHostedPasswordReset,
  resendHostedVerification,
  safeBrandColor,
  safeHostedImageURL,
  safeHostedOrigin,
  startGoogleProvider,
  type HostedPortalContext,
  type EmailActionContext,
  type MCPAuthorizationContext,
  type MCPOwnedProject,
} from "@/lib/hostedPortal";
import { isTurnstileEnabled } from "@/lib/turnstile";
import { firstPartyLoginPath, mcpAuthorizePath } from "@/routes";

export type HostedAuthAction =
  | "signin"
  | "signup"
  | "forgot-password"
  | "reset-password"
  | "verify-email"
  | "authorize"
  | "mcp-authorize";

function usePortalMessages(): AuthMessages {
  // Resolve from the browser on each mount path; no server locale or picker.
  return getAuthMessages();
}

export function HostedAuthPortal({ action }: { action: HostedAuthAction }) {
  const { projectSlug } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user, loading: authLoading, refresh } = useAuth();
  const userRef = useRef(user);
  userRef.current = user;
  const t = usePortalMessages();
  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const transaction = query.get("transaction") ?? "";
  const [context, setContext] = useState<HostedPortalContext | null>(null);
  const [mcpContext, setMcpContext] = useState<MCPAuthorizationContext | null>(
    null,
  );
  const [emailContext, setEmailContext] = useState<EmailActionContext | null>(
    null,
  );
  const actionToken = useRef(query.get("token") ?? "");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);
  const isPlatformEntry =
    !projectSlug && (action === "signin" || action === "signup");
  const isMCPAuthorize = action === "mcp-authorize";
  const platformSessionRestoring =
    (isPlatformEntry || isMCPAuthorize) && authLoading;
  const platformSessionActive = isPlatformEntry && user?.status === "active";

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    setMcpContext(null);
    const isTokenAction =
      (action === "verify-email" || action === "reset-password") &&
      actionToken.current !== "";
    const continuation = query.get("continue") ?? "/dashboard/projects";

    // The app-level auth provider restores the persistent HttpOnly platform
    // session before the sign-in portal creates a new transaction. Waiting for
    // it avoids racing two refresh rotations and avoids showing a login form
    // to an owner whose access cookie merely expired between visits.
    if (isPlatformEntry) {
      if (platformSessionRestoring) {
        return () => {
          active = false;
        };
      }
      if (platformSessionActive) {
        navigate(continuation, { replace: true });
        return () => {
          active = false;
        };
      }
    }

    if (isMCPAuthorize) {
      if (platformSessionRestoring) {
        return () => {
          active = false;
        };
      }
      if (!transaction) {
        setError(new Error(t.mcpTransactionRequired));
        setLoading(false);
        return () => {
          active = false;
        };
      }
      // Auth v2 standby (US-110 / D-0070 / BL-071): portal transaction login
      // and third-party Application OAuth are disabled. MCP consent stays live
      // and uses the first-party dashboard session. Signed-out owners go to
      // /login?continue=… — never /portal/platform/signin.
      let redirectedToFirstPartyLogin = false;
      void getMCPAuthorizationContext(transaction)
        .then((next) => {
          if (!active) return;
          const signedIn = userRef.current;
          if (signedIn?.status === "active") {
            setMcpContext({
              ...next,
              authenticated: true,
              signed_in_user: next.signed_in_user ?? {
                id: signedIn.id,
                email: signedIn.email ?? undefined,
              },
            });
            return;
          }
          if (next.authenticated) {
            setMcpContext(next);
            return;
          }
          redirectedToFirstPartyLogin = true;
          navigate(firstPartyLoginPath(mcpAuthorizePath(transaction)), {
            replace: true,
          });
        })
        .catch((reason: unknown) => {
          if (active) setError(reason);
        })
        .finally(() => {
          if (active && !redirectedToFirstPartyLogin) setLoading(false);
        });
      return () => {
        active = false;
      };
    }

    const request = isTokenAction
      ? getEmailActionContext(
          projectSlug ? "project" : "platform",
          projectSlug,
          action,
          actionToken.current,
        )
      : projectSlug
        ? transaction
          ? getProjectPortalContext(projectSlug, transaction)
          : Promise.reject(new Error(t.authorizationTransactionRequired))
        : createPlatformPortalContext(continuation, transaction);
    void request
      .then((next) => {
        if (!active) return;
        if (isTokenAction) {
          setEmailContext(next as EmailActionContext);
          window.history.replaceState(
            window.history.state,
            "",
            `${location.pathname}?action=ready`,
          );
          return;
        }
        const portal = next as HostedPortalContext;
        setContext(portal);
        // Keep the opaque portal transaction in the address bar so sign-in ↔
        // sign-up and refresh resume the same CSRF-bound handle. Use
        // history.replaceState (not navigate) to avoid re-running this effect
        // and rotating CSRF a second time before the user can submit.
        if (
          !projectSlug &&
          portal.transaction &&
          query.get("transaction") !== portal.transaction
        ) {
          const params = new URLSearchParams(location.search);
          params.set("transaction", portal.transaction);
          if (!params.get("continue") && portal.continuation) {
            params.set("continue", portal.continuation);
          }
          window.history.replaceState(
            window.history.state,
            "",
            `${location.pathname}?${params.toString()}`,
          );
        }
      })
      .catch((reason: unknown) => {
        if (active) setError(reason);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [
    action,
    isMCPAuthorize,
    isPlatformEntry,
    location.pathname,
    location.search,
    navigate,
    platformSessionActive,
    platformSessionRestoring,
    projectSlug,
    query,
    transaction,
  ]);

  // After a platform sign-in/sign-up the server only sets the HttpOnly session
  // cookie and returns no token, so the React auth state is still signed-out.
  // Refresh it from the cookie before navigating, otherwise the destination
  // (e.g. /projects) sees `user === null` and bounces back to /login until a
  // manual page reload remounts AuthProvider.
  async function completePlatformAuthentication(continuation: string) {
    if (context?.realm === "platform") {
      await refresh();
    }
    navigate(continuation, { replace: true });
  }

  if (loading) {
    return (
      <HostedShell context={null} t={t}>
        <Spinner label={t.loadingContext} center className="min-h-48" />
      </HostedShell>
    );
  }

  if (error || (!context && !emailContext && !mcpContext)) {
    return (
      <HostedShell context={null} t={t}>
        <div role="alert" className="space-y-4">
          <h1 className="font-sans text-xl font-semibold">
            {t.linkUnavailableTitle}
          </h1>
          <ErrorBox error={error ?? new Error(t.invalidAuthContext)} />
          <p className="text-sm text-text-muted">{t.linkUnavailableBody}</p>
        </div>
      </HostedShell>
    );
  }

  if (emailContext) {
    return (
      <HostedEmailAction
        context={emailContext}
        token={actionToken.current}
        t={t}
      />
    );
  }

  if (action === "mcp-authorize" && mcpContext) {
    return <MCPAuthorizationReview mcp={mcpContext} t={t} />;
  }

  if (!context) return null;

  if (action === "authorize") {
    if (!context.authenticated) {
      return (
        <HostedCredentialsForm
          context={context}
          action="signin"
          t={t}
          onComplete={(continuation) =>
            navigate(continuation, { replace: true })
          }
        />
      );
    }
    return <HostedAuthorizationReview context={context} t={t} />;
  }

  if (
    action === "forgot-password" ||
    action === "reset-password" ||
    action === "verify-email"
  ) {
    return <HostedLifecycle context={context} action={action} t={t} />;
  }

  if (context.authenticated) {
    const continuation =
      context.realm === "platform"
        ? (context.continuation ?? "/dashboard/projects")
        : `/auth/${encodeURIComponent(context.project_slug ?? "")}/authorize?transaction=${encodeURIComponent(context.transaction)}`;
    return (
      <HostedExistingSession
        context={context}
        t={t}
        onContinue={() => navigate(continuation, { replace: true })}
      />
    );
  }

  if (action !== "signin" && action !== "signup") {
    return null;
  }

  return (
    <HostedCredentialsForm
      context={context}
      action={action}
      t={t}
      onComplete={(continuation) =>
        void completePlatformAuthentication(continuation)
      }
    />
  );
}

function HostedExistingSession({
  context,
  onContinue,
  t,
}: {
  context: HostedPortalContext;
  onContinue: () => void;
  t: AuthMessages;
}) {
  return (
    <HostedShell context={context} t={t}>
      <div className="space-y-5">
        <CheckCircle2 className="h-8 w-8 text-secondary" aria-hidden="true" />
        <div>
          <p className="eyebrow">{t.sessionFound}</p>
          <h1 className="mt-2 font-sans text-2xl font-semibold">
            {context.realm === "platform"
              ? t.continueToDashboard
              : t.continueToApp(context.application_name ?? "")}
          </h1>
          <p className="mt-2 text-sm text-text-muted">{t.sessionSkipHint}</p>
        </div>
        <Button className="w-full" type="button" onClick={onContinue}>
          {t.continue}
        </Button>
      </div>
    </HostedShell>
  );
}

type IdentifierMode = "email" | "username";

function HostedCredentialsForm({
  context,
  action,
  onComplete,
  t,
}: {
  context: HostedPortalContext;
  action: "signin" | "signup";
  onComplete: (continuation: string) => void;
  t: AuthMessages;
}) {
  const { setSession } = useAuth();
  const [identifierMode, setIdentifierMode] =
    useState<IdentifierMode>("email");
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const isSignup = action === "signup";
  const isProject = context.realm === "project";
  const basePath =
    context.realm === "platform" ? "/auth" : `/auth/${context.project_slug}`;
  const turnstileOn = isTurnstileEnabled(context.turnstile);
  // Signup is always-on captcha when Turnstile is configured. Sign-in is adaptive
  // server-side; still show the widget whenever sitekey is present so tokens are
  // available after CAPTCHA_REQUIRED (and on MCP authorize return-to-consent).
  const captchaRequired = isSignup || turnstileOn;
  const [captchaHardRequired, setCaptchaHardRequired] = useState(isSignup);

  function switchIdentifierMode(next: IdentifierMode) {
    setIdentifierMode(next);
    if (next === "email") {
      setUsername("");
    } else {
      setEmail("");
    }
  }

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const result = await authenticateHostedPortal(context, action, {
        email:
          !isProject || identifierMode === "email"
            ? email || undefined
            : undefined,
        username:
          isProject && identifierMode === "username"
            ? username || undefined
            : undefined,
        password,
        turnstile_token: turnstileToken || undefined,
      });
      // Cross-origin HTTP (dev.local → api.dev.local): cookies may not stick;
      // platform sign-in returns session tokens for localStorage Bearer auth.
      if (result.session) {
        setSession(result.session);
      }
      onComplete(result.continuation);
    } catch (reason) {
      setError(reason);
      setTurnstileToken("");
      resetTurnstileWidgets();
      const code = reason instanceof ApiClientError ? reason.code : "";
      const message =
        reason instanceof Error ? reason.message.toLowerCase() : "";
      if (
        code === "CAPTCHA_REQUIRED" ||
        code === "CAPTCHA_INVALID" ||
        message.includes("turnstile")
      ) {
        setCaptchaHardRequired(true);
      }
    } finally {
      setBusy(false);
    }
  }

  async function useGoogle() {
    setBusy(true);
    setError(null);
    try {
      const result = await startGoogleProvider(context);
      window.location.assign(result.authorization_url);
    } catch (reason) {
      setError(reason);
      setBusy(false);
    }
  }

  const heading =
    context.realm === "platform"
      ? isSignup
        ? t.createDashboard
        : t.signInDashboard
      : isSignup
        ? t.createAccountFor(context.application_name ?? "")
        : t.continueToAppHeading(context.application_name ?? "");

  const mustHaveTurnstileToken =
    turnstileOn && (isSignup || captchaHardRequired);

  return (
    <HostedShell context={context} t={t}>
      <header className="mb-6">
        <p className="eyebrow">
          {context.realm === "platform"
            ? t.platformAccount
            : context.project_name}
        </p>
        <h1 className="mt-2 font-sans text-2xl font-semibold tracking-tight text-text-primary">
          {heading}
        </h1>
        <p className="mt-2 text-sm leading-relaxed text-text-muted">
          {context.realm === "platform"
            ? t.platformIdentityHint
            : t.projectIdentityHint}
        </p>
      </header>
      <form onSubmit={onSubmit} className="space-y-4">
        {error ? (
          <div aria-live="polite">
            <ErrorBox error={error} />
          </div>
        ) : null}
        {isProject ? (
          <div>
            {identifierMode === "email" ? (
              <>
                <Label htmlFor="hosted-email">{t.email}</Label>
                <Input
                  id="hosted-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </>
            ) : (
              <>
                <Label htmlFor="hosted-username">{t.username}</Label>
                <Input
                  id="hosted-username"
                  autoComplete="username"
                  required
                  value={username}
                  onChange={(event) => setUsername(event.target.value)}
                />
              </>
            )}
            <button
              type="button"
              className="mt-2 text-sm font-medium text-secondary hover:text-secondary"
              data-testid="identifier-mode-switch"
              onClick={() =>
                switchIdentifierMode(
                  identifierMode === "email" ? "username" : "email",
                )
              }
            >
              {identifierMode === "email"
                ? t.useUsernameInstead
                : t.useEmailInstead}
            </button>
          </div>
        ) : (
          <div>
            <Label htmlFor="hosted-email">{t.email}</Label>
            <Input
              id="hosted-email"
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>
        )}
        <div>
          <Label htmlFor="hosted-password">{t.password}</Label>
          <Input
            id="hosted-password"
            type="password"
            autoComplete={isSignup ? "new-password" : "current-password"}
            minLength={8}
            required
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
        </div>
        <TurnstileWidget
          turnstile={context.turnstile}
          action={isSignup ? "signup" : "signin"}
          onToken={setTurnstileToken}
        />
        <Button
          className="w-full"
          type="submit"
          disabled={
            busy ||
            (mustHaveTurnstileToken && !turnstileToken) ||
            (captchaRequired && isSignup && turnstileOn && !turnstileToken)
          }
        >
          {busy ? (
            <>
              <ProgressRing size="sm" label={t.pleaseWait} />
              {t.pleaseWait}
            </>
          ) : isSignup ? (
            t.createAccount
          ) : (
            t.signIn
          )}
        </Button>
      </form>
      {isProject ? (
        <div className="mt-4 border-t border-border-standard pt-4">
          <Button
            className="w-full"
            type="button"
            variant="outline"
            disabled={busy}
            onClick={() => void useGoogle()}
          >
            {t.continueWithGoogle}
          </Button>
          <p className="mt-2 text-center text-xs text-text-muted">
            {t.googleConsentHint}
          </p>
        </div>
      ) : null}
      <div className="mt-5 flex items-center justify-between gap-3 text-sm">
        <Link
          className="font-medium text-secondary hover:text-secondary"
          to={`${basePath}/${isSignup ? "sign-in" : "sign-up"}?transaction=${encodeURIComponent(context.transaction)}`}
        >
          {isSignup ? t.alreadyHaveAccount : t.createAnAccount}
        </Link>
        {!isSignup ? (
          <Link
            className="text-text-muted hover:text-text-primary"
            to={`${basePath}/forgot-password?transaction=${encodeURIComponent(context.transaction)}`}
          >
            {t.forgotPassword}
          </Link>
        ) : null}
      </div>
    </HostedShell>
  );
}

/** Interactive MCP agent consent: client name, owned-project picker, plain scopes. */
function MCPAuthorizationReview({
  mcp,
  t,
}: {
  mcp: MCPAuthorizationContext;
  t: AuthMessages;
}) {
  const navigate = useNavigate();
  const [projects, setProjects] = useState<MCPOwnedProject[]>([]);
  const [projectId, setProjectId] = useState("");
  const requestedAllProjects = [
    ...(mcp.scopes ?? []),
    ...(mcp.scope_details ?? []).map((row) => row.scope),
  ].includes("mcp:all_projects");
  const [allProjectsApproved, setAllProjectsApproved] = useState(false);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [busy, setBusy] = useState<"confirm" | "deny" | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loadingProjects, setLoadingProjects] = useState(true);
  const callbackOrigin = safeHostedOrigin(mcp.redirect_uri);

  useEffect(() => {
    let active = true;
    setLoadingProjects(true);
    void listOwnedProjectsForMCP()
      .then((list) => {
        if (!active) return;
        const activeProjects = list.filter((p) => p.status === "active");
        setProjects(activeProjects);
        if (activeProjects.length === 1) {
          setProjectId(activeProjects[0].id);
        }
      })
      .catch((reason: unknown) => {
        if (active) setLoadError(reason);
      })
      .finally(() => {
        if (active) setLoadingProjects(false);
      });
    return () => {
      active = false;
    };
  }, []);

  async function decide(decision: "confirm" | "deny") {
    setBusy(decision);
    setError(null);
    try {
      if (decision === "confirm" && !projectId) {
        throw new Error(t.selectProjectError);
      }
      if (decision === "confirm" && requestedAllProjects && !allProjectsApproved) {
        throw new Error(t.selectAllProjectsError);
      }
      const result = await decideMCPAuthorization(
        mcp.transaction,
        decision,
        decision === "confirm" ? projectId : undefined,
        decision === "confirm" && allProjectsApproved,
      );
      window.location.assign(result.redirect_to);
    } catch (reason) {
      setError(reason);
      setBusy(null);
    }
  }

  const scopeRows = (
    mcp.scope_details?.length > 0
      ? mcp.scope_details
      : (mcp.scopes ?? []).map((scope) => ({ scope, description: "" }))
  ).map((row) => ({
    ...row,
    description: mcpScopeExplanationMessage(row.scope, t),
  }));

  return (
    <HostedShell context={null} t={t}>
      <div className="space-y-5">
        <div>
          <p className="eyebrow">{t.agentAuthRequired}</p>
          <h1 className="mt-2 font-sans text-2xl font-semibold">
            {t.connectAgent(mcp.client_name)}
          </h1>
          <p className="mt-2 text-sm text-text-muted">
            {t.agentConsentNeverAuto}
          </p>
        </div>
        {error || loadError ? (
          <div aria-live="polite">
            <ErrorBox error={error ?? loadError} />
          </div>
        ) : null}
        <div className="rounded-lg border border-border-standard bg-white p-3 text-sm">
          <p className="font-medium text-text-primary">
            {mcp.signed_in_user?.email ?? t.platformOwner}
          </p>
          <p className="text-xs text-text-muted">{t.signedInAsOwner}</p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="mt-2"
            onClick={() =>
              navigate(firstPartyLoginPath(mcpAuthorizePath(mcp.transaction)), {
                replace: true,
              })
            }
          >
            {t.changeAccount}
          </Button>
        </div>
        <div className="rounded-lg border border-border-standard bg-surface-container-low p-4">
          <label className="text-sm font-medium" htmlFor="mcp-project-picker">
            {t.projectAgentMayAccess}
          </label>
          <p className="mt-1 text-xs text-text-muted">
            {requestedAllProjects ? t.allProjectsRequested : t.oneProjectPerAuth}
          </p>
          {loadingProjects ? (
            <Spinner
              label={t.loadingProjects}
              size="sm"
              center={false}
              className="mt-3"
            />
          ) : projects.length === 0 ? (
            <p className="mt-3 text-sm text-text-muted">{t.noActiveProjects}</p>
          ) : (
            <select
              id="mcp-project-picker"
              className="input mt-3 w-full"
              value={projectId}
              onChange={(event) => setProjectId(event.target.value)}
              required
            >
              <option value="">{t.selectProject}</option>
              {projects.map((project) => (
                <option key={project.id} value={project.id}>
                  {project.name} ({project.slug})
                </option>
              ))}
            </select>
          )}
          {requestedAllProjects ? (
            <label className="mt-4 flex items-start gap-3 rounded-md border border-primary-strong/25 bg-white p-3 text-sm text-text-secondary">
              <input
                type="checkbox"
                className="mt-0.5 h-4 w-4 accent-secondary"
                checked={allProjectsApproved}
                onChange={(event) => setAllProjectsApproved(event.target.checked)}
                aria-describedby="mcp-all-projects-help"
              />
              <span>
                <span className="font-medium text-text-primary">
                  {t.allProjectsConsent}
                </span>
                <span id="mcp-all-projects-help" className="mt-1 block text-xs text-text-muted">
                  {t.allProjectsConsentDesc}
                </span>
              </span>
            </label>
          ) : null}
        </div>
        <div className="rounded-lg border border-border-standard bg-surface-container-low p-4">
          <p className="text-sm font-medium">{t.permissionsRequested}</p>
          <ul className="mt-3 space-y-2" aria-label="Requested MCP permissions">
            {scopeRows.map((row) => (
              <li
                key={row.scope}
                className="flex items-start gap-2 text-sm text-text-secondary"
              >
                <CheckCircle2
                  className="mt-0.5 h-4 w-4 shrink-0 text-secondary"
                  aria-hidden="true"
                />
                <span>
                  <span className="font-medium text-text-primary">
                    {mcpScopeLabelMessage(row.scope, t)}
                  </span>
                  <span className="mt-0.5 block text-xs text-text-muted">
                    {row.description}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-lg border border-border-standard p-4 text-xs leading-relaxed text-text-muted">
          <p>
            <span className="font-medium text-text-primary">
              {t.agentCallback}
            </span>{" "}
            {callbackOrigin}
          </p>
          <p className="mt-1">
            <span className="font-medium text-text-primary">{t.mcpResource}</span>{" "}
            <span className="font-mono">{mcp.resource}</span>
          </p>
          <p className="mt-1">{t.tokensShortLived}</p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Button
            type="button"
            variant="outline"
            disabled={busy !== null}
            onClick={() => void decide("deny")}
          >
            {busy === "deny" ? t.cancelling : t.cancel}
          </Button>
          <Button
            type="button"
            disabled={
              busy !== null ||
              loadingProjects ||
              projects.length === 0 ||
              !projectId ||
              (requestedAllProjects && !allProjectsApproved)
            }
            onClick={() => void decide("confirm")}
          >
            {busy === "confirm" ? t.authorizing : t.authorizeAgent}
          </Button>
        </div>
      </div>
    </HostedShell>
  );
}

function HostedAuthorizationReview({
  context,
  t,
}: {
  context: HostedPortalContext;
  t: AuthMessages;
}) {
  const navigate = useNavigate();
  const [busy, setBusy] = useState<"confirm" | "deny" | "change" | null>(null);
  const [error, setError] = useState<unknown>(null);
  const callbackOrigin = safeHostedOrigin(context.redirect_uri);

  async function decide(decision: "confirm" | "deny") {
    setBusy(decision);
    setError(null);
    try {
      const result = await decideHostedAuthorization(context, decision);
      window.location.assign(result.redirect_to);
    } catch (reason) {
      setError(reason);
      setBusy(null);
    }
  }

  async function changeAccount() {
    setBusy("change");
    setError(null);
    try {
      await changeHostedAccount(context);
      navigate(
        `/auth/${encodeURIComponent(context.project_slug ?? "")}/sign-in?transaction=${encodeURIComponent(context.transaction)}`,
        { replace: true },
      );
    } catch (reason) {
      setError(reason);
      setBusy(null);
    }
  }

  return (
    <HostedShell context={context} t={t}>
      <div className="space-y-5">
        <div>
          <p className="eyebrow">{t.authorizationRequired}</p>
          <h1 className="mt-2 font-sans text-2xl font-semibold">
            {t.reviewAccessFor(context.application_name ?? "")}
          </h1>
          <p className="mt-2 text-sm text-text-muted">{t.consentNeverAuto}</p>
          {context.application_description ? (
            <p className="mt-3 text-sm leading-relaxed text-text-secondary">
              {context.application_description}
            </p>
          ) : null}
        </div>
        {error ? (
          <div aria-live="polite">
            <ErrorBox error={error} />
          </div>
        ) : null}
        <div className="flex items-center justify-between gap-3 rounded-lg border border-border-standard bg-white p-3 text-sm">
          <div className="min-w-0">
            <p className="truncate font-medium text-text-primary">
              {context.signed_in_user?.email ??
                context.signed_in_user?.username ??
                t.signedInProjectUser}
            </p>
            <p className="text-xs text-text-muted">
              {t.signedInTo(context.project_name ?? "")}
            </p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={busy !== null}
            onClick={() => void changeAccount()}
          >
            {t.changeAccount}
          </Button>
        </div>
        <div className="rounded-lg border border-border-standard bg-surface-container-low p-4">
          <p className="text-sm font-medium">{t.requestedPermissions}</p>
          <ul className="mt-3 space-y-2" aria-label="Requested permissions">
            {(context.scopes ?? []).map((scope) => (
              <li
                key={scope}
                className="flex items-start gap-2 text-sm text-text-secondary"
              >
                <CheckCircle2
                  className="mt-0.5 h-4 w-4 shrink-0 text-secondary"
                  aria-hidden="true"
                />
                <span>
                  <span className="font-medium text-text-primary">
                    {scopeLabelMessage(scope, t)}
                  </span>
                  <span className="mt-0.5 block text-xs text-text-muted">
                    {scopeExplanationMessage(scope, t)}
                  </span>
                </span>
              </li>
            ))}
          </ul>
        </div>
        <div className="rounded-lg border border-border-standard p-4 text-xs leading-relaxed text-text-muted">
          <p>
            <span className="font-medium text-text-primary">{t.destination}</span>{" "}
            {callbackOrigin}
          </p>
          <p className="mt-1">{t.redirectRegistrationHint}</p>
          {context.developer_name ? (
            <p className="mt-2">{t.publishedBy(context.developer_name)}</p>
          ) : null}
        </div>
        {context.previous_grant ? (
          <p className="text-xs text-text-muted">
            {t.previousGrant(context.previous_grant.authorization_count)}
          </p>
        ) : null}
        <div className="grid grid-cols-2 gap-3">
          <Button
            type="button"
            variant="outline"
            disabled={busy !== null}
            onClick={() => void decide("deny")}
          >
            {busy === "deny" ? t.cancelling : t.cancel}
          </Button>
          <Button
            type="button"
            disabled={busy !== null}
            onClick={() => void decide("confirm")}
          >
            {busy === "confirm" ? t.authorizing : t.authorizeAndContinue}
          </Button>
        </div>
      </div>
    </HostedShell>
  );
}

function HostedLifecycle({
  context,
  action,
  t,
}: {
  context: HostedPortalContext;
  action: HostedAuthAction;
  t: AuthMessages;
}) {
  const [email, setEmail] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const labels = {
    "forgot-password": t.recoverAccount,
    "reset-password": t.setNewPassword,
    "verify-email": t.verifyYourEmail,
  } as const;
  const turnstileAction =
    action === "forgot-password" ? "forgot" : "resend";

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      if (action === "forgot-password") {
        await requestHostedPasswordReset(context, email, turnstileToken);
      } else {
        await resendHostedVerification(context, turnstileToken);
      }
      setComplete(true);
    } catch (reason) {
      setError(reason);
      setTurnstileToken("");
      resetTurnstileWidgets();
    } finally {
      setBusy(false);
    }
  }

  return (
    <HostedShell context={context} t={t}>
      <div className="space-y-4">
        <KeyRound className="h-7 w-7 text-secondary" aria-hidden="true" />
        <h1 className="font-sans text-2xl font-semibold">
          {labels[action as keyof typeof labels]}
        </h1>
        {complete ? (
          <p role="status" className="text-sm leading-relaxed text-text-muted">
            {t.recoverySent}
          </p>
        ) : (
          <form className="space-y-4" onSubmit={submit}>
            <p className="text-sm leading-relaxed text-text-muted">
              {t.recoveryBoundHint(context.realm)}
            </p>
            {error ? <ErrorBox error={error} /> : null}
            {action === "forgot-password" ? (
              <div>
                <Label htmlFor="recovery-email">{t.email}</Label>
                <Input
                  id="recovery-email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                />
              </div>
            ) : null}
            <TurnstileWidget
              turnstile={context.turnstile}
              action={turnstileAction}
              onToken={setTurnstileToken}
            />
            <Button
              className="w-full"
              type="submit"
              disabled={
                busy ||
                (isTurnstileEnabled(context.turnstile) && !turnstileToken)
              }
            >
              {busy ? (
                <>
                  <ProgressRing size="sm" label={t.pleaseWait} />
                  {t.pleaseWait}
                </>
              ) : action === "forgot-password" ? (
                t.sendResetInstructions
              ) : (
                t.resendVerification
              )}
            </Button>
          </form>
        )}
      </div>
    </HostedShell>
  );
}

function HostedEmailAction({
  context,
  token,
  t,
}: {
  context: EmailActionContext;
  token: string;
  t: AuthMessages;
}) {
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [turnstileToken, setTurnstileToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const isReset = context.purpose === "password_reset";
  const shellContext: HostedPortalContext = {
    realm: context.realm,
    transaction: "",
    csrf_token: context.csrf_token,
    expires_at: context.expires_at,
    project_slug: context.project_slug,
    project_name: context.project_name,
    application_name: context.application_name,
    application_logo: context.application_logo,
    support_contact: context.support_contact,
    turnstile: context.turnstile,
  };

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (isReset && password !== confirmPassword) {
      setError(new Error(t.passwordsDoNotMatch));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await completeHostedEmailAction(
        context,
        token,
        password,
        turnstileToken,
      );
      setComplete(true);
    } catch (reason) {
      setError(reason);
      setTurnstileToken("");
      resetTurnstileWidgets();
    } finally {
      setBusy(false);
    }
  }

  return (
    <HostedShell context={shellContext} t={t}>
      <div className="space-y-4">
        {complete ? (
          <>
            <CheckCircle2 className="h-8 w-8 text-secondary" aria-hidden="true" />
            <h1 className="font-sans text-2xl font-semibold">
              {isReset ? t.passwordUpdated : t.emailVerified}
            </h1>
            <p role="status" className="text-sm text-text-muted">
              {isReset ? t.passwordUpdatedBody : t.emailVerifiedBody}
            </p>
            {context.realm === "platform" ? (
              <Button className="w-full" asChild>
                <Link to="/auth/sign-in">{t.continueToSignIn}</Link>
              </Button>
            ) : null}
          </>
        ) : (
          <form className="space-y-4" onSubmit={submit}>
            <KeyRound className="h-7 w-7 text-secondary" aria-hidden="true" />
            <h1 className="font-sans text-2xl font-semibold">
              {isReset ? t.setNewPassword : t.verifyYourEmail}
            </h1>
            <p className="text-sm text-text-muted">
              {t.oneTimeActionHint(
                new Date(context.expires_at).toLocaleString(),
                context.realm,
              )}
            </p>
            {error ? <ErrorBox error={error} /> : null}
            {isReset ? (
              <>
                <div>
                  <Label htmlFor="new-password">{t.newPassword}</Label>
                  <Input
                    id="new-password"
                    type="password"
                    autoComplete="new-password"
                    minLength={8}
                    maxLength={128}
                    required
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                  />
                </div>
                <div>
                  <Label htmlFor="confirm-password">{t.confirmPassword}</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    autoComplete="new-password"
                    minLength={8}
                    maxLength={128}
                    required
                    value={confirmPassword}
                    onChange={(event) => setConfirmPassword(event.target.value)}
                  />
                </div>
              </>
            ) : null}
            <TurnstileWidget
              turnstile={context.turnstile}
              action={isReset ? "reset" : "verify"}
              onToken={setTurnstileToken}
            />
            <Button
              className="w-full"
              type="submit"
              disabled={
                busy ||
                (isTurnstileEnabled(context.turnstile) && !turnstileToken)
              }
            >
              {busy ? (
                <>
                  <ProgressRing size="sm" label={t.pleaseWait} />
                  {t.pleaseWait}
                </>
              ) : isReset ? (
                t.updatePassword
              ) : (
                t.verifyEmailCta
              )}
            </Button>
          </form>
        )}
      </div>
    </HostedShell>
  );
}

function HostedShell({
  context,
  children,
  t,
}: {
  context: HostedPortalContext | null;
  children: React.ReactNode;
  t: AuthMessages;
}) {
  const [logoFailed, setLogoFailed] = useState(false);
  const logo = safeHostedImageURL(context?.application_logo);
  const privacyURL = safeHostedImageURL(context?.privacy_policy_url);
  const termsURL = safeHostedImageURL(context?.terms_url);
  const branding = context?.branding;
  const style = {
    "--portal-primary": safeBrandColor(branding?.primary_color, "#F48120"),
    "--portal-accent": safeBrandColor(branding?.accent_color, "#0054CA"),
    "--portal-background": safeBrandColor(
      branding?.background_color,
      "#F3F3F3",
    ),
    "--portal-text": safeBrandColor(branding?.text_color, "#1A1C1C"),
  } as CSSProperties;
  return (
    <main
      className="hosted-portal min-h-screen min-w-0 overflow-x-hidden px-4 py-8 sm:py-14"
      style={style}
    >
      <div className="mx-auto w-full max-w-md">
        <div className="mb-5 flex items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            {logo && !logoFailed ? (
              <img
                src={logo}
                alt=""
                className="h-10 w-10 rounded-lg border border-border-standard bg-white object-contain p-1"
                referrerPolicy="no-referrer"
                onError={() => setLogoFailed(true)}
              />
            ) : (
              <div
                className="flex h-10 w-10 items-center justify-center rounded-sm bg-primary text-sm font-bold text-on-primary shadow-none"
                aria-label="Application logo fallback"
              >
                {(context?.application_name ?? "TB").slice(0, 2).toUpperCase()}
              </div>
            )}
            <div className="min-w-0">
              <p className="truncate font-sans text-sm font-semibold text-text-primary">
                {context?.application_name ?? "TinyBase"}
              </p>
              <p className="truncate font-mono text-[10px] uppercase tracking-label text-text-muted">
                {context?.developer_name ||
                  context?.project_name ||
                  t.hostedAuthentication}
              </p>
            </div>
          </div>
          {context?.eVerify === true ? (
            <span className="inline-flex shrink-0 items-center gap-1 text-xs font-medium text-secondary">
              <ShieldCheck className="h-4 w-4" aria-hidden="true" />
              {t.verified}
            </span>
          ) : null}
        </div>
        <section className="rounded-xl border border-border-standard bg-surface-container-lowest p-6 shadow-none sm:p-7">
          {children}
        </section>
        <footer className="mt-5 space-y-3 text-center text-xs text-text-muted">
          <p className="inline-flex items-center gap-1.5 font-medium text-text-secondary">
            <ShieldCheck className="h-4 w-4 text-secondary" aria-hidden="true" />
            {t.poweredBy}
          </p>
          <nav
            aria-label="Application policies"
            className="flex justify-center gap-4"
          >
            {privacyURL ? (
              <a href={privacyURL} target="_blank" rel="noreferrer">
                {t.privacy}
              </a>
            ) : null}
            {termsURL ? (
              <a href={termsURL} target="_blank" rel="noreferrer">
                {t.terms}
              </a>
            ) : null}
            <a
              href="https://tinybase.dev/security"
              target="_blank"
              rel="noreferrer"
            >
              {t.security}
            </a>
          </nav>
        </footer>
      </div>
    </main>
  );
}
